/**
 * Phase 2 MM15 — Production / QA tab (project studio).
 * Create workflow → dashboard (stage, QA status, readiness, owner-test/
 * real-team-test/provider-activation flags) → QA checklist → revisions →
 * final delivery → gate + owner-test decision → exports. Internal
 * workflow only; QA gate is a control, not certification; provider
 * activation is a separate pending step.
 */

import { useCallback, useEffect, useState } from "react";
import {
  addProductionDecision, createProductionRevision, createProductionWorkflow,
  exportOwnerTestPack, exportProductionQaReport, getFinalDelivery,
  getProductionAssignments, getProductionChecklist, getProductionGate,
  getProductionRevisions, getProductionWorkflow, patchFinalDelivery,
  patchProductionChecklist, resolveProductionRevision,
} from "../api/client";
import { Button } from "../components/Button";
import { StatusPill } from "../components/StatusPill";
import type { StudioStrings } from "../i18n/studioStrings";

type Row = Record<string, unknown>;
const QA_STATUSES = ["pending", "passed", "failed", "waived_with_note",
  "not_applicable", "needs_info"];

export function ProductionQaTab({ projectId, t }:
  { projectId: string; t: StudioStrings }): JSX.Element {
  const [wf, setWf] = useState<Row | null>(null);
  const [exists, setExists] = useState<boolean | null>(null);
  const [section, setSection] = useState("dashboard");
  const [checklist, setChecklist] = useState<Row[]>([]);
  const [revisions, setRevisions] = useState<Row[]>([]);
  const [assignments, setAssignments] = useState<Row[]>([]);
  const [delivery, setDelivery] = useState<Row | null>(null);
  const [gate, setGate] = useState<Row | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await getProductionWorkflow(projectId);
    if (r.outcome !== "ok") { setNotice(r.status === 403 ? t.permissionDenied : t.loadError); return; }
    setExists(r.data?.exists ?? false);
    setWf((r.data?.item as Row) ?? null);
  }, [projectId, t.permissionDenied, t.loadError]);

  useEffect(() => { void load(); }, [load]);

  const wfId = wf ? String(wf.id) : "";

  const loadSection = useCallback(async (sec: string) => {
    setSection(sec);
    if (!wf) return;
    if (sec === "checklist") {
      const r = await getProductionChecklist(projectId, wfId);
      setChecklist(r.outcome === "ok" ? r.data?.items ?? [] : []);
    } else if (sec === "revisions") {
      const r = await getProductionRevisions(projectId, wfId);
      setRevisions(r.outcome === "ok" ? r.data?.items ?? [] : []);
    } else if (sec === "assignments") {
      const r = await getProductionAssignments(projectId, wfId);
      setAssignments(r.outcome === "ok" ? r.data?.items ?? [] : []);
    } else if (sec === "delivery") {
      const r = await getFinalDelivery(projectId, wfId);
      setDelivery(r.outcome === "ok" ? (r.data?.item as Row) : null);
    } else if (sec === "gate") {
      const r = await getProductionGate(projectId, wfId);
      setGate(r.outcome === "ok" ? (r.data as Row) : null);
    }
  }, [wf, wfId, projectId]);

  async function onCreate(): Promise<void> {
    const r = await createProductionWorkflow(projectId);
    setNotice(r.outcome === "ok" ? null
      : r.status === 403 ? t.permissionDenied : r.errorMessage);
    await load();
  }

  async function onDelivery(field: string, value: boolean): Promise<void> {
    await patchFinalDelivery(projectId, wfId, { [field]: value });
    await loadSection("delivery"); await load();
  }

  async function onDecision(decision: string): Promise<void> {
    const note = decision === "approved_for_owner_test" ? "approved"
      : window.prompt(t.notesLabel) ?? "";
    const r = await addProductionDecision(projectId, wfId, decision, note);
    setNotice(r.outcome === "ok" ? `${t.qaGate}: ${decision}` : r.errorMessage);
    await loadSection("gate"); await load();
  }

  if (exists === false) {
    return (
      <section data-testid="project-production">
        <p className="po-disclaimer" data-testid="production-honesty">{t.internalWorkflowOnly}</p>
        {notice ? <p role="alert" className="po-error">{notice}</p> : null}
        <p data-testid="production-empty">{t.emptyList}</p>
        <Button variant="primary" onClick={() => void onCreate()}>{t.createWorkflow}</Button>
      </section>);
  }
  if (!wf) return <section data-testid="project-production"><p role="status">…</p></section>;

  return (
    <section data-testid="project-production">
      <p className="po-disclaimer" data-testid="production-honesty">{t.internalWorkflowOnly}</p>
      {notice ? <p role="alert" className="po-error">{notice}</p> : null}
      <nav className="po-tabs">
        {[["dashboard", t.productionTab], ["checklist", t.qaChecklist],
          ["revisions", t.revisions], ["assignments", t.roleLabel],
          ["delivery", t.finalDelivery], ["gate", t.qaGate]].map(([k, label]) => (
          <button key={k} type="button" aria-pressed={section === k}
                  className={section === k ? "po-tab po-tab--active" : "po-tab"}
                  onClick={() => void loadSection(k)}>{label}</button>))}
      </nav>

      {section === "dashboard" ? (
        <div data-testid="production-dashboard">
          <p>{t.statusLabel}: <StatusPill tone={String(wf.qa_status).startsWith("approved")
            ? "ok" : "info"} label={String(wf.qa_status)} /> · {t.readinessScore}:{" "}
            {String(wf.readiness_score)}% · stage {String(wf.current_stage)}</p>
          <ul>
            <li>{t.ownerTestReady}: {wf.owner_test_ready ? "✅" : "⏳"}</li>
            <li>real_team_test_ready: {wf.real_team_test_ready ? "✅" : "⏳"}</li>
            <li>provider_activation_ready: {wf.provider_activation_ready ? "✅" : "⏳"} ·{" "}
              <em>{t.providerActivationPending}</em></li>
          </ul>
          {Array.isArray(wf.blocking_reasons) && wf.blocking_reasons.length ? (
            <p data-testid="production-blockers">🚫 {(wf.blocking_reasons as string[])
              .slice(0, 4).join(" · ")}</p>) : null}
          <p><strong>next:</strong> {String(wf.next_recommended_action)}</p>
          <Button variant="secondary" onClick={() => void exportProductionQaReport(projectId, wfId)
            .then((r) => setNotice(r.outcome === "ok" ? "QA report exported" : r.errorMessage))}>
            {t.exportsSection}
          </Button>{" "}
          <Button variant="secondary" onClick={() => void exportOwnerTestPack(projectId, wfId)
            .then((r) => setNotice(r.outcome === "ok" ? "Owner test pack exported" : r.errorMessage))}>
            {t.realTeamTestPlan}
          </Button>
        </div>) : null}

      {section === "checklist" ? (
        <ul data-testid="production-checklist">
          {checklist.map((c) => (
            <li key={String(c.id)}>
              <StatusPill tone={c.severity === "blocker" || c.severity === "high"
                ? "warn" : "info"} label={String(c.severity)} />{" "}
              [{String(c.group_key)}] {String(c.label)}{" "}
              <select value={String(c.status)}
                      onChange={(e) => void patchProductionChecklist(projectId, wfId,
                        String(c.id), e.target.value,
                        e.target.value === "waived_with_note" ? "waived" : "")
                        .then(() => loadSection("checklist")).then(load)}>
                {QA_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </li>))}
        </ul>) : null}

      {section === "revisions" ? (
        <div data-testid="production-revisions">
          <Button variant="secondary" onClick={() => void createProductionRevision(projectId, wfId,
            { revision_type: "general", severity: "medium",
              title: window.prompt("revision title") ?? "Revision" })
            .then(() => loadSection("revisions")).then(load)}>{t.addRevision}</Button>
          <ul>
            {revisions.map((r) => (
              <li key={String(r.id)}>
                <StatusPill tone={r.severity === "blocker" ? "warn" : "info"}
                            label={String(r.severity)} />{" "}
                {String(r.title)} — {String(r.status)}
                {r.status === "open" || r.status === "in_progress" ? (
                  <> <Button variant="secondary" onClick={() =>
                    void resolveProductionRevision(projectId, wfId, String(r.id),
                      window.prompt(t.notesLabel) ?? "resolved")
                      .then(() => loadSection("revisions")).then(load)}>resolve</Button></>) : null}
              </li>))}
            {revisions.length === 0 ? <li>{t.emptyList}</li> : null}
          </ul>
        </div>) : null}

      {section === "assignments" ? (
        <ul data-testid="production-assignments">
          {assignments.map((a) => (
            <li key={String(a.id)}>{String(a.role)} — {String(a.status)}</li>))}
        </ul>) : null}

      {section === "delivery" ? (
        <div data-testid="production-delivery">
          {delivery ? (
            <ul>
              {["internal_notes_excluded", "supplier_contacts_excluded",
                "margin_notes_excluded", "storage_keys_excluded",
                "honesty_block_confirmed"].map((f) => (
                <li key={f}>
                  <label><input type="checkbox" checked={Boolean(delivery[f])}
                    onChange={(e) => void onDelivery(f, e.target.checked)} /> {f}</label>
                </li>))}
              <li>status: {String(delivery.checklist_status)}</li>
            </ul>) : <p>{t.emptyList}</p>}
        </div>) : null}

      {section === "gate" ? (
        <div data-testid="production-gate">
          <p>{t.qaGate}: <strong>{String(gate?.qa_status ?? wf.qa_status)}</strong> ·{" "}
            {t.ownerTestReady}: {gate?.owner_test_ready ? "✅" : "⏳"}</p>
          {Array.isArray(gate?.blocking_reasons) && (gate!.blocking_reasons as string[]).length ? (
            <ul>{(gate!.blocking_reasons as string[]).map((r, i) => <li key={i}>🚫 {r}</li>)}</ul>) : null}
          <Button variant="primary" onClick={() => void onDecision("approved_for_owner_test")}>
            {t.approveOwnerTest}
          </Button>{" "}
          <Button variant="secondary" onClick={() => void onDecision("revisions_required")}>
            revisions_required
          </Button>
          <p className="po-disclaimer">{t.internalWorkflowOnly}</p>
        </div>) : null}
    </section>);
}
